import checkIsStorageError from '@anm/helpers/checkIsStorageError';
import useIsMountedCheck from '@anm/hooks/useIsMountedCheck';
import useVideoStatusChecker from '@anm/hooks/useVideoStatusChecker';
import { parseManifest, supportedExtensions, useVideoData, VideoStatusCode } from '@anm/hosting-player';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnimatronProject } from 'types/project';

import { CustomizationSectionProps } from '..';
import { hostedVideoActions } from '../../../../store/modules/hostedVideo';
import { projectSelectors } from '../../../../store/modules/project';
import { streamActions } from '../../../../store/modules/stream';
import { userActions, userSelectors } from '../../../../store/modules/user';

import { brandActions } from './../../../../store/modules/brands';
import { landingsActions } from './../../../../store/modules/landings';
import useFetchLanding from './useFetchLanding';
import useFetchProjectStreams from './useFetchProjectStreams';
import useHostingUserStatus from './useHostingUserStatus';

const { setVideoData, cleanHostedVideoStore } = hostedVideoActions;
const { cleanLandingsStore } = landingsActions;
const { selectCurrentProject } = projectSelectors;
const { clearUploadedBrand } = brandActions;

const useCustomizationSectionInit = ({ hostId }: CustomizationSectionProps) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [errorCode, setErrorCode] = useState<VideoStatusCode>();
  const storageStats = useSelector(userSelectors.selectUserStorageStats);
  const isStorageError = checkIsStorageError({ storageStats: storageStats.data || null });

  useEffect(() => {
    !storageStats.data && dispatch(userActions.fetchStorageStats());
  }, [storageStats.data]);

  const currentProject = useSelector(selectCurrentProject);

  useFetchProjectStreams();
  useFetchLanding(hostId, currentProject);
  const hostingUserBlockedStatus = useHostingUserStatus();

  const dispatch = useDispatch();
  const isMounted = useIsMountedCheck();

  const { videoData, errorCode: videoErrorCode, errorMessage } = useVideoData({
    source: isStorageError ? 'cdn' : 'api',
    hostId
  });

  const isManifestInProcess = videoErrorCode === VideoStatusCode.in_process;

  const { isMediaReady: isVideoReady, videoManifest } = useVideoStatusChecker({
    hostId,
    source: 'api',
    supportedExtensions,
    progress: isManifestInProcess ? 100 : null
  });

  const userEmbeds = useSelector(userSelectors.selectUserEmbeds);

  const getEmbedStatus = (currentProject: AnimatronProject) => !!currentProject.embeddedAt;

  useEffect(() => {
    !userEmbeds && dispatch(userActions.fetchEmbedStats());
  }, [userEmbeds]);

  useEffect(() => {
    isVideoReady && setErrorCode(undefined);
  }, [isVideoReady]);

  useEffect(() => {
    videoErrorCode && videoErrorCode !== VideoStatusCode.protected && setErrorCode(videoErrorCode);
  }, [videoErrorCode]);

  useEffect(() => {
    hostingUserBlockedStatus && setErrorCode(VideoStatusCode.blocked);
  }, [hostingUserBlockedStatus]);

  useEffect(
    () => () => {
      dispatch(cleanHostedVideoStore());
      dispatch(cleanLandingsStore());
      dispatch(clearUploadedBrand());
      dispatch(streamActions.clearStream());
    },
    []
  );

  useEffect(() => {
    if (!videoData || !currentProject) return;

    dispatch(
      setVideoData({
        hostId,
        videoData,
        embedStatus: getEmbedStatus(currentProject)
      })
    );
  }, [videoData, currentProject]);

  useEffect(() => {
    if (!videoManifest || !currentProject) return;

    dispatch(
      setVideoData({
        hostId,
        videoData: parseManifest(videoManifest),
        embedStatus: getEmbedStatus(currentProject)
      })
    );
  }, [videoManifest, currentProject]);

  return [errorCode, isMounted, currentTime, errorMessage, hostingUserBlockedStatus, setCurrentTime] as const;
};

export default useCustomizationSectionInit;
